import {
  emailStorage,
  logout,
  refreshTokenStorage,
  roleStorage,
  tokenStorage,
  userStorage,
} from '@/modules/infrastructure/users/services/auth.api';
import { ApartmentOutlined } from '@ant-design/icons';
import { history, useIntl, useModel } from '@umijs/max';
import { Button, message, Modal, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  isPositionContext,
  loadUserContexts,
  switchContextAndReload,
} from '../services/contextSwitch';
import type { UserContextItem } from '../types/userContext.types';
import { translateOrgStructureName } from '../utils/orgStructureLabel';

/**
 * Автовход в единственную позицию делаем один раз на вкладку: если бэкенд
 * контекст не сменит, повторная попытка после перезагрузки зациклит страницу.
 */
const AUTO_ENTER_FLAG = 'user_context_auto_enter';

function positionLabel(intl: ReturnType<typeof useIntl>, item: UserContextItem): string {
  return (
    translateOrgStructureName(intl, item.org_structure_name) ||
    item.organization_name ||
    item.org_structure_id
  );
}

/**
 * Работа в системе идёт от имени позиции: только её токен несёт права одной
 * роли. В основном профиле права всех позиций складываются, поэтому, пока
 * позиция не выбрана, работать нельзя.
 */
export const PositionGate: React.FC = () => {
  const intl = useIntl();
  const { initialState } = useModel('@@initialState');
  const [contexts, setContexts] = useState<UserContextItem[]>([]);
  const [entering, setEntering] = useState<string | null>(null);

  const authorized = Boolean(initialState?.currentUser);

  useEffect(() => {
    if (!authorized) return undefined;
    let cancelled = false;

    loadUserContexts()
      .then(({ current, contexts: available }) => {
        if (cancelled || isPositionContext(current) || available.length === 0) return;

        if (available.length === 1) {
          // Выбирать не из чего — входим в единственную позицию молча.
          if (sessionStorage.getItem(AUTO_ENTER_FLAG)) return;
          sessionStorage.setItem(AUTO_ENTER_FLAG, '1');
          setEntering(available[0].org_structure_id);
          switchContextAndReload(available[0].org_structure_id).catch(() => {
            if (!cancelled) setEntering(null);
          });
          return;
        }

        setContexts(available);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [authorized]);

  const enter = async (orgStructureId: string) => {
    if (entering) return;
    try {
      setEntering(orgStructureId);
      await switchContextAndReload(orgStructureId);
    } catch {
      setEntering(null);
      message.error(
        intl.formatMessage({
          id: 'component.contextSwitcher.switchFailed',
          defaultMessage: 'Failed to switch context',
        }),
      );
    }
  };

  /** Единственный выход из окна, если переключиться не удаётся. */
  const signOut = async () => {
    try {
      await logout({ skipErrorHandler: true });
    } catch {
      // Выходим локально в любом случае.
    }
    tokenStorage.remove();
    refreshTokenStorage.remove();
    userStorage.remove();
    emailStorage.remove();
    roleStorage.remove();
    history.replace('/user/login');
  };

  if (!authorized || contexts.length === 0) return null;

  return (
    <Modal
      centered
      closable={false}
      footer={null}
      keyboard={false}
      maskClosable={false}
      open
      title={intl.formatMessage({
        id: 'component.positionGate.title',
        defaultMessage: 'Select a position',
      })}
      width={420}
    >
      <Typography.Paragraph type="secondary">
        {intl.formatMessage({
          id: 'component.positionGate.description',
          defaultMessage: 'You work in the system on behalf of one position.',
        })}
      </Typography.Paragraph>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {contexts.map((context) => (
          <Button
            block
            disabled={Boolean(entering) && entering !== context.org_structure_id}
            icon={<ApartmentOutlined />}
            key={context.org_structure_id}
            loading={entering === context.org_structure_id}
            onClick={() => enter(context.org_structure_id)}
            size="large"
            style={{ justifyContent: 'flex-start', textAlign: 'left' }}
          >
            {positionLabel(intl, context)}
          </Button>
        ))}
      </div>
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Button disabled={Boolean(entering)} onClick={signOut} type="link">
          {intl.formatMessage({
            id: 'component.avatarDropdown.logout',
            defaultMessage: 'Logout',
          })}
        </Button>
      </div>
    </Modal>
  );
};

export default PositionGate;
